import { clamp, snapToGrid } from '@/core/math/grid';
import type { Vec2 } from '@/core/types';

/**
 * Viewport math for the scene (zoom, pan, snapping). Phase 1 renders the identity viewport; the
 * pure functions exist now so the scene builder (Phase 14) and ladder editor reuse one tested
 * coordinate model. Scene coordinates are in scene units; screen coordinates are CSS pixels
 * relative to the scene element.
 */
export interface Viewport {
  readonly zoom: number;
  readonly panX: number;
  readonly panY: number;
}

export const DEFAULT_VIEWPORT: Viewport = { zoom: 1, panX: 0, panY: 0 };
export const ZOOM_LIMITS = { min: 0.25, max: 4 } as const;
/** Distance between grid dots in scene units. */
export const GRID_SIZE = 20;

export function screenToScene(viewport: Viewport, screen: Vec2): Vec2 {
  return {
    x: (screen.x - viewport.panX) / viewport.zoom,
    y: (screen.y - viewport.panY) / viewport.zoom,
  };
}

export function sceneToScreen(viewport: Viewport, scene: Vec2): Vec2 {
  return {
    x: scene.x * viewport.zoom + viewport.panX,
    y: scene.y * viewport.zoom + viewport.panY,
  };
}

/** Scales the viewport by `factor` while keeping the scene point under `anchor` fixed on screen. */
export function zoomAt(viewport: Viewport, factor: number, anchor: Vec2): Viewport {
  const zoom = clamp(viewport.zoom * factor, ZOOM_LIMITS.min, ZOOM_LIMITS.max);
  const anchorInScene = screenToScene(viewport, anchor);
  return {
    zoom,
    panX: anchor.x - anchorInScene.x * zoom,
    panY: anchor.y - anchorInScene.y * zoom,
  };
}

export function panBy(viewport: Viewport, dx: number, dy: number): Viewport {
  return { ...viewport, panX: viewport.panX + dx, panY: viewport.panY + dy };
}

export function snapPointToGrid(point: Vec2, gridSize: number = GRID_SIZE): Vec2 {
  return { x: snapToGrid(point.x, gridSize), y: snapToGrid(point.y, gridSize) };
}
